import { useState, useEffect, useCallback, useRef } from "react";
import { AGENT_BASE_URL, AGENT_POLL_INTERVAL_MS } from "../constants";

export type AgentStatus = "detecting" | "connected" | "unavailable";

export interface AgentInstalledApp {
  id: string;
  displayName: string;
  version: string;
  packageName: string;
  installPath: string;
}

export interface AgentInfo {
  agentVersion: string;
  os: { platform: string; arch: string; release: string };
  installedApps: AgentInstalledApp[];
}

export function useAgentConnection() {
  const [status, setStatus] = useState<AgentStatus>("detecting");
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2000);
      const res = await fetch(`${AGENT_BASE_URL}/status`, { signal: controller.signal });
      clearTimeout(timeout);
      if (res.ok) {
        const data = await res.json();
        setAgentInfo(data);
        setStatus("connected");
      } else {
        setStatus("unavailable");
        setAgentInfo(null);
      }
    } catch {
      setStatus("unavailable");
      setAgentInfo(null);
    }
  }, []);

  useEffect(() => {
    check();
    intervalRef.current = setInterval(check, AGENT_POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [check]);

  /** Optimistically remove an app from the local agentInfo (no network call) */
  const removeApp = useCallback((appId: string) => {
    setAgentInfo((prev) =>
      prev ? { ...prev, installedApps: prev.installedApps.filter((a) => a.id !== appId) } : prev,
    );
  }, []);

  return { status, agentInfo, retry: check, removeApp };
}
